import PropTypes from 'prop-types';
import { useState, useRef, useEffect } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { Typography, LinearProgress, Box, Tooltip } from '@mui/material';
import { fCurrency, fPercent } from '../../../../utils/formatNumber';
import { calculatePpd } from '../../../../utils/calculatePpd';
import Iconify from '../../../../reusable-components/iconify';
import { HistoryModal } from './index';

BudgetBar.propTypes = {
  budget: PropTypes.number,
  total: PropTypes.number,
  history: PropTypes.number,
  budgetDate: PropTypes.any,
  includeUnapproved: PropTypes.bool,
  catId: PropTypes.number,
  facId: PropTypes.any,
  subcategoryId: PropTypes.number,
  tab: PropTypes.number,
  isDisplayingPPDValues: PropTypes.bool,
  censusAmount: PropTypes.number,
  amountDaysInBudget: PropTypes.number,
};

export default function BudgetBar({
  budget,
  total,
  history,
  includeUnapproved,
  budgetDate,
  isDisplayingPPDValues,
  catId,
  facId,
  subcategoryId,
  tab,
  censusAmount,
  amountDaysInBudget
}) {
  const [openModal, setOpenModal] = useState(false);
  const spendRef = useRef(null);
  const totalRef = useRef(null);
  const [spendOverflow, setSpendOverflow] = useState(false);
  const [totalOverflow, setTotalOverflow] = useState(false);

  useEffect(() => {
    if (spendRef.current) {
      setSpendOverflow(spendRef.current.scrollWidth > spendRef.current.clientWidth);
    }
    if (totalRef.current) {
      setTotalOverflow(totalRef.current.scrollWidth > totalRef.current.clientWidth);
    }
  }, [history, total, budget]);

  const theme = useTheme();

  const handleClickOpen = () => {
    setOpenModal(true);
  };
  const determineColor = (value) => {
    if (value > 76) {
      return '#EB5757';
    }
    if (value > 50) {
      return '#F2C94C';
    }
    return theme.palette.info.main;
  };

  const historyPpd = calculatePpd(history, censusAmount, amountDaysInBudget);
  const totalPpd = calculatePpd(total, censusAmount, amountDaysInBudget);

  return (
    <Box sx={{ flexGrow: 1, position: 'relative', mt: 1 }}>
      <Box
        sx={{'&:hover': { cursor: 'pointer'}}}
        onClick={handleClickOpen}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          <Typography variant="subtitle2" sx={{fontWeight: 500}}>
            Spend History:{' '}
          </Typography>

          <Typography
            variant="subtitle2"
            component="span"
            ref={spendRef}
            sx={{
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              gap: 0.5,
              fontWeight: 500
            }}
          >
            {isDisplayingPPDValues && historyPpd !== null ? `${fCurrency(historyPpd)} PPD /` : null}
            {history - budget > 0 ? (
              <>
                {`${fCurrency(history || 0.0)} (${
                  budget === 0 ? fPercent(0) : fPercent(Math.floor((history / budget) * 100))
                }) `}
                {spendOverflow ? (
                  <Tooltip title={`${fCurrency(history - budget)} over budget`} arrow>
                    <span>...</span>
                  </Tooltip>
                ) : (
                  ` ${fCurrency(history - budget)} over budget`
                )}
              </>
            ) : history === 0 ? (
              '$0.00'
            ) : (
              `${fCurrency(history || 0.0)} (${
                budget === 0 ? fPercent(0) : fPercent(Math.floor((history / budget) * 100))
              })`
            )}
            <Iconify icon={'fluent:window-new-20-regular'} height={16} width={16} />
          </Typography>
        </Box>
      </Box>
      <StyledTopLinearProgress
        variant="determinate"
        value={budget === 0 ? 0 : (history / budget) * 100 > 100 ? 100 : (history / budget) * 100}
        sx={{
          borderBottom: `2px solid ${determineColor(budget === 0 ? 0 : (history / budget) * 100)}`,
          '& .MuiLinearProgress-bar': {
            bgcolor: determineColor(budget === 0 ? 0 : (history / budget) * 100),
          },
        }}
      />
      <HistoryModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        includeUnapproved={includeUnapproved}
        budgetDate={budgetDate}
        catId={catId}
        facId={facId}
        subcategoryId={subcategoryId}
        tab={tab}
      />
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          mt: 1,
        }}
      >
        <Typography variant="subtitle2" sx={{fontWeight: 500}}>
          Total:
        </Typography>

        <Typography
          variant="subtitle2"
          component="span"
          ref={totalRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            gap: 0.5,
            fontWeight: 500
          }}
        >
          {isDisplayingPPDValues && totalPpd !== null ? `${fCurrency(totalPpd)} PPD / ` : null}
          {total - budget > 0 ? (
            <>
              {`${fCurrency(total || 0.0)} (${
                budget === 0 ? fPercent(0) : fPercent(Math.floor((total / budget) * 100))
              }) `}
              {totalOverflow ? (
                <Tooltip title={`${fCurrency(total - budget)} over budget`} arrow>
                  <span>...</span>
                </Tooltip>
              ) : (
                ` ${fCurrency(total - budget)} over budget`
              )}
            </>
          ) : total === 0 ? (
            '$0.00'
          ) : (
            `${fCurrency(total || 0.0)} (${
              budget === 0 ? fPercent(0) : fPercent(Math.floor((total / budget) * 100))
            })`
          )}
        </Typography>
      </Box>
      <StyledBottomLinearProgress
        variant="determinate"
        value={budget === 0 ? 0 : (total / budget) * 100 > 100 ? 100 : (total / budget) * 100}
        sx={{
          borderBottom: `2px solid ${determineColor(budget === 0 ? 0 : (total / budget) * 100)}`,
          '& .MuiLinearProgress-bar': {
            bgcolor: determineColor(budget === 0 ? 0 : (total / budget) * 100),
          },
        }}
      />
    </Box>
  );
}
const StyledTopLinearProgress = styled(LinearProgress)(({ theme }) => ({
  backgroundColor: '#fff',
  '& .MuiLinearProgress-barColorPrimary': {
    backgroundColor: theme.palette.primary.lightest,
  },
  '& .MuiLinearProgress-barColorInfo': {
    backgroundColor: theme.palette.info.lighter,
  },
  '& .MuiLinearProgress-barColorError': {
    backgroundColor: theme.palette.error.lighter,
  },
  '& .MuiLinearProgress-barColorWarning': {
    backgroundColor: theme.palette.warning.lighter,
  },
}));

const StyledBottomLinearProgress = styled(LinearProgress)(({ theme }) => ({
  borderTop: 'none',
  backgroundColor: '#fff',
  '& .MuiLinearProgress-barColorPrimary': {
    backgroundColor: theme.palette.primary.main,
  },
  '& .MuiLinearProgress-barColorError': {
    backgroundColor: theme.palette.error.main,
  },
  '& .MuiLinearProgress-barColorWarning': {
    backgroundColor: theme.palette.warning.main,
  },
}));


